import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, Suspense, lazy } from 'react';
import { MainLayout } from './components/layout/main-layout';
import { QuickActionsButton } from './components/help/quick-actions-button';
import { CookieConsent } from './components/legal/cookie-consent';
import { ADAWidget } from './components/accessibility/ada-widget';
import { GOOGLE_ADS_ID } from './utils/analytics';
// Pre-import SEOHead to ensure it's available for lazy-loaded components
import './components/seo/seo-head';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/home').then(m => ({ default: m.HomePage })));
const LandingPage = lazy(() => import('./pages/landing'));
const ServicesPage = lazy(() => import('./pages/services').then(m => ({ default: m.ServicesPage })));
const ContactPage = lazy(() => import('./pages/contact').then(m => ({ default: m.ContactPage })));
const ShopPage = lazy(() => import('./pages/shop').then(m => ({ default: m.ShopPage })));
const SupportPage = lazy(() => import('./pages/support').then(m => ({ default: m.SupportPage })));
const AboutPage = lazy(() => import('./pages/about'));
const PrivacyPage = lazy(() => import('./pages/privacy'));
const CookiePolicyPage = lazy(() => import('./pages/cookies'));
const TermsPage = lazy(() => import('./pages/terms'));
// EU DSA Resolution page removed - US-only service
// const EUDSAResolutionPage = lazy(() => import('./pages/eu-dsa-resolution'));
const BlogPage = lazy(() => import('./pages/blog').then(m => ({ default: m.BlogPage })));
const ResourcesPage = lazy(() => import('./pages/resources').then(m => ({ default: m.ResourcesPage })));
const FireAlarmMaintenanceChecklistPage = lazy(() => import('./pages/fire-alarm-maintenance-checklist').then(m => ({ default: m.FireAlarmMaintenanceChecklistPage })));
const NFPA72ComplianceGuidePage = lazy(() => import('./pages/nfpa-72-compliance-guide').then(m => ({ default: m.NFPA72ComplianceGuidePage })));
const AccessControlBuyersGuidePage = lazy(() => import('./pages/access-control-buyers-guide').then(m => ({ default: m.AccessControlBuyersGuidePage })));
const DoorKingRemoteAccessSetupPage = lazy(() => import('./pages/doorking-remote-access-setup').then(m => ({ default: m.DoorKingRemoteAccessSetupPage })));
const CaliforniaMonitoringCompliancePage = lazy(() => import('./pages/california-monitoring-compliance').then(m => ({ default: m.CaliforniaMonitoringCompliancePage })));
const SanFranciscoFireCodeRequirementsPage = lazy(() => import('./pages/san-francisco-fire-code-requirements').then(m => ({ default: m.SanFranciscoFireCodeRequirementsPage })));
const FireAlarmMaintenanceGuidePage = lazy(() => import('./pages/fire-alarm-maintenance-guide').then(m => ({ default: m.FireAlarmMaintenanceGuidePage })));

// Service Pages - Lazy loaded
const CCTVServicePage = lazy(() => import('./pages/services/cctv-service').then(m => ({ default: m.CCTVServicePage })));
const AccessControlPage = lazy(() => import('./pages/services/access-control').then(m => ({ default: m.AccessControlPage })));
const BurglarAlarmPage = lazy(() => import('./pages/services/burglar-alarm').then(m => ({ default: m.BurglarAlarmPage })));
const MonitoringPage = lazy(() => import('./pages/services/monitoring').then(m => ({ default: m.MonitoringPage })));
const FireAlarmPage = lazy(() => import('./pages/services/fire-alarm').then(m => ({ default: m.FireAlarmPage })));
const ERRCSPage = lazy(() => import('./pages/services/errcs').then(m => ({ default: m.ERRCSPage })));
const ConsultingPage = lazy(() => import('./pages/services/consulting').then(m => ({ default: m.ConsultingPage })));
const MaintenancePage = lazy(() => import('./pages/services/maintenance').then(m => ({ default: m.MaintenancePage })));

// Quote Pages - Lazy loaded
const CCTVQuotePage = lazy(() => import('./pages/quotes/cctv-quote').then(m => ({ default: m.CCTVQuotePage })));
const AccessControlQuotePage = lazy(() => import('./pages/quotes/access-control-quote').then(m => ({ default: m.AccessControlQuotePage })));
const BurglarAlarmQuotePage = lazy(() => import('./pages/quotes/burglar-alarm-quote').then(m => ({ default: m.BurglarAlarmQuotePage })));
const MonitoringQuotePage = lazy(() => import('./pages/quotes/monitoring-quote').then(m => ({ default: m.MonitoringQuotePage })));
const FireAlarmQuotePage = lazy(() => import('./pages/quotes/fire-alarm-quote').then(m => ({ default: m.FireAlarmQuotePage })));
const QuoteSuccessPage = lazy(() => import('./pages/quotes/quote-success').then(m => ({ default: m.QuoteSuccessPage })));

// Loading component with accessibility
function PageLoader() {
  return (
    <div 
      className="flex min-h-screen items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label="Loading page"
    >
      <div className="text-center">
        <div 
          className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#e69138]"
          aria-hidden="true"
        />
        <p className="mt-4 text-gray-600">
          <span className="sr-only">Loading, please wait</span>
          <span aria-hidden="true">Loading...</span>
        </p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <GtagRouteChangeTracker />
      <HashScrollHandler />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/fire-alarm-maintenance-checklist" element={<FireAlarmMaintenanceChecklistPage />} />
            <Route path="/resources/nfpa-72-compliance-guide" element={<NFPA72ComplianceGuidePage />} />
            <Route path="/resources/access-control-buyers-guide" element={<AccessControlBuyersGuidePage />} />
            <Route path="/resources/doorking-remote-access-setup" element={<DoorKingRemoteAccessSetupPage />} />
            <Route path="/resources/california-monitoring-compliance" element={<CaliforniaMonitoringCompliancePage />} />
            <Route path="/resources/san-francisco-fire-code-requirements" element={<SanFranciscoFireCodeRequirementsPage />} />
            <Route path="/blog/fire-alarm-maintenance-guide" element={<FireAlarmMaintenanceGuidePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* EU DSA Resolution route removed - US-only service */}
            
            {/* Service Routes */}
            <Route path="/services/cctv-systems" element={<CCTVServicePage />} />
            <Route path="/services/access-control" element={<AccessControlPage />} />
            <Route path="/services/burglar-alarms" element={<BurglarAlarmPage />} />
            <Route path="/services/24-7-monitoring" element={<MonitoringPage />} />
            <Route path="/services/fire-alarm" element={<FireAlarmPage />} />
            <Route path="/services/errcs" element={<ERRCSPage />} />
            <Route path="/services/consulting" element={<ConsultingPage />} />
            <Route path="/services/maintenance" element={<MaintenancePage />} />
            <Route path="/services/support" element={<SupportPage />} />
            
            {/* Quote Routes */}
            <Route path="/quotes/services/cctv-systems" element={<CCTVQuotePage />} />
            <Route path="/quotes/services/access-control" element={<AccessControlQuotePage />} />
            <Route path="/quotes/services/burglar-alarms" element={<BurglarAlarmQuotePage />} />
            <Route path="/quotes/services/24-7-monitoring" element={<MonitoringQuotePage />} />
            <Route path="/quotes/services/errcs" element={<MonitoringQuotePage />} />
            <Route path="/quotes/services/fire-alarm" element={<FireAlarmQuotePage />} />
            <Route path="/quotes/success" element={<QuoteSuccessPage />} />
          </Route>

          {/* Archived splash route without main layout (no header/footer) */}
          <Route path="/landing" element={<LandingPage />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* Quick Actions Button */}
      <QuickActionsButton mode="fixed" />
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      {/* ADA Accessibility Widget */}
      <ADAWidget />
    </BrowserRouter>
  );
}

// Sends a page_view to gtag on route changes (for SPA tracking)
function GtagRouteChangeTracker() {
  const location = useLocation();
  const hasTrackedInitialRoute = useRef(false);

  useEffect(() => {
    if (!hasTrackedInitialRoute.current) {
      hasTrackedInitialRoute.current = true;
      return;
    }

    const url = location.pathname + location.search + location.hash;
    // window.gtag may not exist immediately if script hasn't loaded
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', GOOGLE_ADS_ID, {
        page_path: url,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

function HashScrollHandler() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const elementId = decodeURIComponent(location.hash.slice(1));
    const timeout = window.setTimeout(() => {
      document.getElementById(elementId)?.scrollIntoView({ block: 'start' });
    }, 100);

    return () => window.clearTimeout(timeout);
  }, [location.pathname, location.hash]);

  return null;
}
